import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../components/authContext";
import ArrowBackIos from "@mui/icons-material/ArrowBackIos";
import "./MyRequests.css";
import { useRequireListing } from "../hooks/useRequireListing";

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

function formatDateLabel(iso, tz) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: tz });
}

function formatTimeRange(startIso, endIso, tz) {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const opts = { hour: "numeric", minute: "2-digit", timeZone: tz };
  return `${start.toLocaleTimeString([], opts)} – ${end.toLocaleTimeString([], opts)}`;
}

function formatStatus(status) {
  return String(status || "pending")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function BookingCard({ booking, onOpen }) {
  const isPending = booking.status === "pending";
  const tz = booking.hostTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const handleOpen = () => {
    onOpen?.(booking);
  };

  return (
    <div
      className={`mr-item is-clickable ${isPending ? "is-pending" : ""}`}
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleOpen();
        }
      }}
    >
      <div className="mr-item-top">
        <div className="mr-item-title">{booking.customerName || "Customer"}</div>
        <div className="mr-status">
          {isPending && <span className="mr-dot" aria-hidden />}
          <span className={`mr-status-label ${isPending ? "is-strong" : ""}`}>{formatStatus(booking.status)}</span>
        </div>
      </div>
      <div className="mr-item-activity">{booking.activityTitle || "Activity"}</div>
      <div className="mr-item-meta">
        <span>{formatDateLabel(booking.startTime, tz)}</span>
        <span>{formatTimeRange(booking.startTime, booking.endTime, tz)}</span>
      </div>
    </div>
  );
}

export default function MyRequests() {
  const { user, authReady } = useAuth();
  const navigate = useNavigate();
  const { checking: checkingListing, allowed: hasListing } = useRequireListing();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!authReady) return;
    if (user === null) {
      navigate("/login", { replace: true, state: { from: "/requests" } });
    }
  }, [user, authReady, navigate]);

  const goBack = () => navigate("/dashboard");

  useEffect(() => {
    let alive = true;
    async function run() {
      if (!authReady || !user || !hasListing) return;
      try {
        setLoading(true);
        setError("");
        const token = await user.getIdToken();
        const r = await fetch(`${API_BASE_URL}/api/my/requests`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const j = await r.json();
        if (!r.ok) throw new Error(j?.error || "Failed to load requests.");
        if (!alive) return;
        setBookings(Array.isArray(j.bookings) ? j.bookings : []);
      } catch (e) {
        if (alive) setError(e.message || "Failed to load requests.");
      } finally {
        if (alive) setLoading(false);
      }
    }
    run();
    return () => { alive = false; };
  }, [user, authReady, hasListing]);

  const grouped = useMemo(() => {
    const upcomingStatuses = new Set(["accepted", "accepted_needs_payout"]);
    const historyStatuses = new Set(["completed", "cancelled", "declined"]);

    const upcoming = [];
    const pending = [];
    const history = [];

    (bookings || []).forEach((b) => {
      if (b.status === "pending") {
        pending.push(b);
      } else if (upcomingStatuses.has(b.status)) {
        upcoming.push(b);
      } else if (historyStatuses.has(b.status)) {
        history.push(b);
      } else {
        history.push(b); // catch-all
      }
    });

    const sortByStart = (arr) => arr.slice().sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    return {
      upcoming: sortByStart(upcoming),
      pending: sortByStart(pending),
      history: sortByStart(history).reverse(), // newest first in history
    };
  }, [bookings]);

  if (!user || checkingListing) {
    return <div className="mr-container"><div className="mr-panel">Loading…</div></div>;
  }

  if (loading) {
    return <div className="mr-container"><div className="mr-panel">Loading…</div></div>;
  }

  if (error) {
    return (
      <div className="mr-container">
        <div className="mr-panel">
          <div className="mr-error">{error}</div>
          <button className="mr-btn" onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  const sections = [
    { title: "Upcoming Activities", items: grouped.upcoming, empty: "No upcoming activities." },
    { title: "Pending Requests", items: grouped.pending, empty: "No pending requests." },
    { title: "History", items: grouped.history, empty: "No past requests yet." },
  ];

  const openDetail = (booking) => {
    if (!booking?.id) return;
    navigate(`/request/${booking.id}`);
  };

  return (
    <div className="mr-container">
      <div className="mr-panel">
        <button className="pv-back" onClick={goBack} aria-label="Back to dashboard">
          <ArrowBackIos fontSize="small" />
          <span>Back</span>
        </button>
        <div className="mr-head">
          <h1>Requests</h1>
          <p>Review your bookings by status.</p>
        </div>

        {sections.map((section) => (
          <div className="mr-section" key={section.title}>
            <div className="mr-section-title">{section.title}</div>
            {section.items.length === 0 ? (
              <div className="mr-empty">{section.empty}</div>
            ) : (
              <div className="mr-list">
                {section.items.map((b) => <BookingCard key={b.id} booking={{ ...b }} onOpen={openDetail} />)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
